import { setSentryMember } from "@configs/sentry/sentry";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import logger from "@utils/logger";
import {
  AccountModel,
  AuthToken,
  BankConfig,
  Banner,
  bnOrZero,
  CredentialModel,
  MemberFundingProfileSettings,
  ProfileResponse,
  SelfEntityModel,
  WalletType,
} from "kz-ui-sdk";
import { InitAuthPayload } from "./types";
import { persistAccessToken, persistMemberContactBanners, refineFundingProfileSettings, refineSelf } from "./utils";

export type AuthSliceState = {
  oauth?: AuthToken | null;
  self?: SelfEntityModel | null;
  credential?: CredentialModel | null;
  bankInfos?: Record<string, BankConfig> | null;
  ewalletInfos?: Record<string, BankConfig> | null;
  accounts?: AccountModel[] | null;
  contactLink?: string | null;
  contactBanners?: Banner[] | null;
  groupedContactIcon?: string;
  latestDepositAmount?: number;
  isRefreshingToken?: boolean;
  fundingProfileSettings?: MemberFundingProfileSettings;
};

export const initialState: AuthSliceState = {};

export const authSlice = createSlice({
  name: "authSlices",
  initialState,
  reducers: {
    initAuthState(state, action: PayloadAction<InitAuthPayload | null>) {
      const { token = null, accounts = null, credential = null } = action.payload ?? {};
      const self = refineSelf(action.payload?.self);
      state.oauth = token;
      state.self = self;
      state.bankInfos = action.payload?.bankInfos;
      state.ewalletInfos = action.payload?.ewalletInfos;
      state.contactLink = action.payload?.contactLink;
      state.contactBanners = action.payload?.contactBanners;
      state.groupedContactIcon = action.payload?.groupedContactIcon ?? "";
      state.latestDepositAmount = action.payload?.latestDepositAmount;
      state.credential = credential;
      state.fundingProfileSettings = refineFundingProfileSettings(action.payload?.fundingProfileSettings);
      persistAccessToken(token);
      persistMemberContactBanners(action.payload?.contactBanners);
      setSentryMember(self?.member);
    },
    updateAccessToken(state, action: PayloadAction<AuthToken | null>) {
      logger._console.log("update access token", action);
      state.oauth = action.payload;
      persistAccessToken(action.payload);
    },
    resetExpiredAt(state) {
      if (state?.self?.member?.pwExpireAt) {
        state.self.member.pwExpireAt = null;
      }
    },
    updateProfile(state, action: PayloadAction<ProfileResponse | null>) {
      const self = refineSelf(action.payload?.self);
      state.accounts = action.payload?.accounts ?? null;
      state.self = self;
      state.contactLink = action.payload?.contactLink ?? "";
      state.contactBanners = action.payload?.contactBanners ?? [];
      state.groupedContactIcon = action.payload?.groupedContactIcon ?? "";
      state.bankInfos = action.payload?.bankInfos;
      state.ewalletInfos = action.payload?.ewalletInfos;
      state.latestDepositAmount = action.payload?.latestDepositAmount;
      state.credential = action.payload?.credential;
      state.fundingProfileSettings = refineFundingProfileSettings(action.payload?.fundingProfileSettings);
      persistMemberContactBanners(action.payload?.contactBanners);
      setSentryMember(self?.member);
    },
    updateProfileBalance(state, action: PayloadAction<string>) {
      // create temp primaryWallet if not exist
      if (state.self?.member && !state.self?.member?.primaryWallet) {
        state.self.member.primaryWallet = {
          balance: bnOrZero(action.payload),
          accountId: state.self.member.accountId,
          memberId: state.self.member.id,
          type: WalletType.DEFAULT,
          txCount: 0,
          id: state.self.member.id,
        };
      }
      // update state.self.member.primaryWallet.balance with new balance
      if (state.self?.member?.primaryWallet) {
        state.self.member.primaryWallet.balance = bnOrZero(action.payload);
      }
    },
    logout(state, action: PayloadAction<void>) {
      state.oauth = null;
      state.self = null;
      state.accounts = null;
      state.contactLink = null;
      state.contactBanners = null;
      state.credential = null;
      state.groupedContactIcon = "";
      setSentryMember(null);
      persistAccessToken(null);
      persistMemberContactBanners(null);
    },
    progressRefreshAccessToken(
      state,
      action: PayloadAction<{
        isRefreshingToken: boolean;
      }>,
    ) {
      state.isRefreshingToken = action.payload.isRefreshingToken;
    },
  },
});

export default authSlice;
